'use client'

import ConfirmModal from '@/components/confirm-modal'
import MultiSelectFilter from '@/components/multi-select-filter'
import { dispatchPipelineRefresh } from '@/components/sales/SalesPipelineDropdown'
import {
    createPipeline,
    CreatePipelineData,
    CreateStageData,
    deletePipeline,
    getPipelines,
    getSalesConfig,
    PAID_TRAFFIC_CHANNEL_LABELS,
    PAID_TRAFFIC_CHANNELS,
    PaidTrafficChannel,
    PIPELINE_TYPE_LABELS,
    pipelinePaidTrafficChannels,
    PipelineType,
    SalesPipeline,
    updatePipeline,
} from '@/lib/api/sales'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import type { AccessibleCompany } from '@/lib/api/common/interfaces/user.interface'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowForward,
    MdClose,
    MdDelete,
    MdEdit,
    MdFilterList,
    MdLock,
    MdPerson,
    MdShuffle,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import StorageRolePicker from '@/pages/admin/core/storage/storage-role-picker'
import PipelineDistributionFields from './pipeline-distribution-fields'
import PipelineRestrictedBadge from './pipeline-restricted-badge'

export default function PipelinesListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'sales',
        'pipelines'
    )

    const [loading, setLoading] = useState(true)
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])

    // Modal state
    const [showModal, setShowModal] = useState(false)
    const [editingPipeline, setEditingPipeline] =
        useState<SalesPipeline | null>(null)
    const [formName, setFormName] = useState('')
    const [formDescription, setFormDescription] = useState('')
    const [formType, setFormType] = useState<PipelineType>('ACQUISITION')
    const [formIsDefault, setFormIsDefault] = useState(false)
    // O funil é da organização e pertence a uma filial — exceto a triagem (padrão da
    // organização), que pode ficar sem. Ver docs/sales-org-pipelines-and-message-routing.md.
    const [formCompanyId, setFormCompanyId] = useState<number | null>(null)
    const [formIsUnitDefault, setFormIsUnitDefault] = useState(false)
    const [formPaidTrafficChannels, setFormPaidTrafficChannels] = useState<PaidTrafficChannel[]>([])
    const [companies, setCompanies] = useState<AccessibleCompany[]>([])
    const [formAllowMultipleDeals, setFormAllowMultipleDeals] = useState(false)
    const [formStages, setFormStages] = useState<CreateStageData[]>([])
    // Distribuição — docs/sales-pipeline-distribution.md
    const [formOwnerUserId, setFormOwnerUserId] = useState<number | null>(null)
    const [formDistributionGroupId, setFormDistributionGroupId] = useState<number | null>(null)
    // Perfis que veem o funil (mesmo modelo das pastas do storage)
    const [formRoleIds, setFormRoleIds] = useState<number[]>([])
    // Perfis que veem todos os funis (Configurações de Vendas): aparecem marcados e
    // travados no modal, para ficar claro quem enxerga o funil além dos marcados nele.
    const [viewAllRoleIds, setViewAllRoleIds] = useState<number[]>([])
    const [submitting, setSubmitting] = useState(false)

    // Delete
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [pipelineToDelete, setPipelineToDelete] =
        useState<SalesPipeline | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadPipelines()
        }
    }, [user?.companyId])

    const loadPipelines = async () => {
        setLoading(true)
        try {
            const response = await getPipelines()
            if (response.success) {
                setPipelines(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar funis')
            }
        } catch {
            toast.error('Erro ao carregar funis')
        } finally {
            setLoading(false)
        }
    }

    const openCreateModal = () => {
        setEditingPipeline(null)
        setFormName('')
        setFormDescription('')
        setFormType('ACQUISITION')
        setFormIsDefault(false)
        setFormCompanyId(companies.length === 1 ? companies[0].id : null)
        setFormIsUnitDefault(false)
        setFormPaidTrafficChannels([])
        setFormAllowMultipleDeals(false)
        setFormOwnerUserId(null)
        setFormDistributionGroupId(null)
        setFormRoleIds([])
        setFormStages([
            {
                name: 'Prospecção',
                color: '#3B82F6',
                sortOrder: 0,
                probability: 10,
            },
            {
                name: 'Qualificação',
                color: '#8B5CF6',
                sortOrder: 1,
                probability: 30,
            },
            {
                name: 'Proposta',
                color: '#F59E0B',
                sortOrder: 2,
                probability: 60,
            },
            {
                name: 'Negociação',
                color: '#F97316',
                sortOrder: 3,
                probability: 80,
            },
            {
                name: 'Fechamento',
                color: '#22C55E',
                sortOrder: 4,
                probability: 100,
                isWonStage: true,
            },
            {
                name: 'Perdido',
                color: '#EF4444',
                sortOrder: 5,
                probability: 0,
                isLostStage: true,
            },
        ])
        setShowModal(true)
    }

    useEffect(() => {
        getAccessibleCompanies().then(response => {
            if (response.success && response.result) setCompanies(response.result)
        })
        // Sem permissão para ler a configuração de vendas, a lista só não mostra os travados
        getSalesConfig().then(response => {
            if (response.success && response.result) setViewAllRoleIds(response.result.viewAllPipelinesRoleIds ?? [])
        })
    }, [])

    const openEditModal = (pipeline: SalesPipeline) => {
        setEditingPipeline(pipeline)
        setFormName(pipeline.name)
        setFormDescription(pipeline.description || '')
        setFormType(pipeline.type)
        setFormIsDefault(pipeline.isDefault)
        setFormCompanyId(pipeline.companyId ?? null)
        setFormIsUnitDefault(!!pipeline.isUnitDefault)
        setFormPaidTrafficChannels(pipelinePaidTrafficChannels(pipeline))
        setFormAllowMultipleDeals(pipeline.allowMultipleDeals)
        setFormOwnerUserId(pipeline.ownerUserId ?? null)
        setFormDistributionGroupId(pipeline.distributionGroupId ?? null)
        setFormRoleIds((pipeline.roleAccess ?? []).map(access => access.roleId))
        setFormStages([])
        setShowModal(true)
    }

    const handleAddStage = () => {
        setFormStages(prev => [
            ...prev,
            {
                name: '',
                color: '#6B7280',
                sortOrder: prev.length,
                probability: 0,
            },
        ])
    }

    const handleRemoveStage = (index: number) => {
        setFormStages(prev => prev.filter((_, i) => i !== index))
    }

    const handleStageChange = (
        index: number,
        field: keyof CreateStageData,
        value: any
    ) => {
        setFormStages(prev =>
            prev.map((stage, i) =>
                i === index ? { ...stage, [field]: value } : stage
            )
        )
    }

    const handleSubmit = async () => {
        if (!formName.trim()) {
            toast.error('O nome do funil é obrigatório')
            return
        }

        setSubmitting(true)
        try {
            if (editingPipeline) {
                const response = await updatePipeline(editingPipeline.id, {
                    name: formName.trim(),
                    description: formDescription.trim() || undefined,
                    type: formType,
                    companyId: formCompanyId,
                    isDefault: formIsDefault,
                    isUnitDefault: formCompanyId ? formIsUnitDefault : false,
                    paidTrafficChannels: formPaidTrafficChannels,
                    allowMultipleDeals: formAllowMultipleDeals,
                    ownerUserId: formOwnerUserId,
                    distributionGroupId: formDistributionGroupId,
                    roleIds: formRoleIds,
                })
                if (response.success) {
                    toast.success('Funil atualizado com sucesso')
                    setShowModal(false)
                    loadPipelines()
                    dispatchPipelineRefresh()
                } else {
                    toast.error(response.error || 'Erro ao atualizar funil')
                }
            } else {
                const validStages = formStages.filter(s => s.name.trim())
                const data: CreatePipelineData = {
                    name: formName.trim(),
                    description: formDescription.trim() || undefined,
                    type: formType,
                    companyId: formCompanyId,
                    isDefault: formIsDefault,
                    isUnitDefault: formCompanyId ? formIsUnitDefault : false,
                    paidTrafficChannels: formPaidTrafficChannels,
                    allowMultipleDeals: formAllowMultipleDeals,
                    stages: validStages.length > 0 ? validStages : undefined,
                }
                const response = await createPipeline(data)
                if (response.success) {
                    // A criação não aceita dona nem grupo: vão num PATCH logo depois.
                    if (
                        response.result &&
                        (formOwnerUserId || formDistributionGroupId || formRoleIds.length > 0)
                    ) {
                        const distributionResponse = await updatePipeline(response.result.id, {
                            ownerUserId: formOwnerUserId,
                            distributionGroupId: formDistributionGroupId,
                            ...(formRoleIds.length > 0 ? { roleIds: formRoleIds } : {}),
                        })
                        if (!distributionResponse.success) {
                            toast.error(
                                distributionResponse.error ||
                                    'Funil criado, mas não foi possível salvar a dona, o grupo e os perfis'
                            )
                        }
                    }
                    toast.success('Funil criado com sucesso')
                    setShowModal(false)
                    loadPipelines()
                    dispatchPipelineRefresh()
                } else {
                    toast.error(response.error || 'Erro ao criar funil')
                }
            }
        } catch {
            toast.error('Erro ao salvar funil')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = (pipeline: SalesPipeline) => {
        setPipelineToDelete(pipeline)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!pipelineToDelete) return
        setDeleting(true)
        try {
            const response = await deletePipeline(pipelineToDelete.id)
            if (response.success) {
                toast.success('Funil excluído com sucesso')
                setShowDeleteModal(false)
                setPipelineToDelete(null)
                loadPipelines()
                dispatchPipelineRefresh()
            } else {
                toast.error(response.error || 'Erro ao excluir funil')
            }
        } catch {
            toast.error('Erro ao excluir funil')
        } finally {
            setDeleting(false)
        }
    }

    const pipelineTypeBadgeClass = (type: PipelineType) => {
        switch (type) {
            case 'ACQUISITION':
                return 'badge-secondary'
            case 'RETENTION':
                return 'badge-secondary'
            case 'UPSELL':
                return 'badge-accent'
            case 'CUSTOM':
                return 'badge-ghost'
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-6 p-6">
                <div className="flex items-center gap-3">
                    <div className="skeleton w-10 h-10 rounded-xl" />
                    <div>
                        <div className="skeleton h-6 w-48 mb-1" />
                        <div className="skeleton h-4 w-32" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="card bg-base-100 shadow-sm">
                            <div className="card-body">
                                <div className="skeleton h-5 w-40 mb-2" />
                                <div className="skeleton h-4 w-full mb-3" />
                                <div className="flex gap-2">
                                    <div className="skeleton h-6 w-20" />
                                    <div className="skeleton h-6 w-16" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center shadow-lg">
                        <MdFilterList className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-base-content">
                                Funis de Vendas
                            </h1>
                            <span className="badge badge-sm badge-secondary">
                                {pipelines.length}
                            </span>
                        </div>
                        <p className="text-sm text-base-content/60">
                            Gerencie os funis e etapas do processo de vendas
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={openCreateModal}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Criar Funil</span>
                    </button>
                )}
            </div>

            {/* Empty State */}
            {pipelines.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdFilterList className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum funil cadastrado
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Crie funis para gerenciar seu processo de vendas com
                            etapas customizáveis
                        </p>
                        {canCreate && (
                            <button
                                onClick={openCreateModal}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar primeiro funil
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {pipelines.map(pipeline => (
                        <div
                            key={pipeline.id}
                            className="card bg-base-100 shadow-sm border border-base-300/50 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                            onClick={() =>
                                navigate(`/sales/pipelines/${pipeline.id}`)
                            }
                        >
                            <div className="card-body p-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-base-content text-lg truncate">
                                                {pipeline.name}
                                            </h3>
                                            {pipeline.isDefault && (
                                                <span className="badge badge-xs badge-success badge-outline flex-shrink-0">
                                                    Padrão
                                                </span>
                                            )}
                                            {(() => {
                                                const channels = pipelinePaidTrafficChannels(pipeline)
                                                if (!channels.length) return null
                                                const label =
                                                    channels.length === PAID_TRAFFIC_CHANNELS.length
                                                        ? 'todos os canais'
                                                        : channels.map(c => PAID_TRAFFIC_CHANNEL_LABELS[c]).join(', ')
                                                return (
                                                    <span
                                                        className="badge badge-xs badge-info badge-outline flex-shrink-0"
                                                        title={`Padrão do tráfego pago: ${label}`}
                                                    >
                                                        Tráfego pago: {label}
                                                    </span>
                                                )
                                            })()}
                                        </div>
                                        <p className="text-xs text-base-content/50 mb-2">
                                            {pipeline.company?.name ?? 'Organização inteira — sem filial'}
                                            {pipeline.isUnitDefault ? ' · padrão da filial' : ''}
                                        </p>
                                        {pipeline.description && (
                                            <p className="text-sm text-base-content/60 mb-3 line-clamp-2">
                                                {pipeline.description}
                                            </p>
                                        )}
                                    </div>
                                    {!pipeline.active && (
                                        <span className="badge badge-sm badge-error badge-outline flex-shrink-0 ml-2">
                                            Inativo
                                        </span>
                                    )}
                                </div>

                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                    <span
                                        className={`badge badge-sm ${pipelineTypeBadgeClass(pipeline.type)}`}
                                    >
                                        {PIPELINE_TYPE_LABELS[pipeline.type]}
                                    </span>
                                    <span className="badge badge-sm badge-outline">
                                        {pipeline._count?.stages ?? 0} etapas
                                    </span>
                                    <span className="badge badge-sm badge-outline">
                                        {pipeline._count?.deals ?? 0} negócios
                                    </span>
                                    <PipelineRestrictedBadge pipeline={pipeline} />
                                    {pipeline.distributionGroup && (
                                        <span
                                            className="badge badge-sm badge-ghost gap-1 max-w-full"
                                            title="Grupo de distribuição"
                                        >
                                            <MdShuffle className="text-xs flex-shrink-0" />
                                            <span className="truncate">
                                                {pipeline.distributionGroup.name}
                                            </span>
                                        </span>
                                    )}
                                </div>

                                {pipeline.owner && (
                                    <div
                                        className="flex items-center gap-1.5 text-xs text-base-content/60 mb-3"
                                        title="Dona do funil"
                                    >
                                        <MdPerson className="text-sm flex-shrink-0" />
                                        <span className="truncate">{pipeline.owner.name}</span>
                                    </div>
                                )}

                                <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                    <div
                                        className="flex items-center gap-1"
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {canUpdate && (
                                            <button
                                                className="btn btn-ghost btn-xs"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    openEditModal(pipeline)
                                                }}
                                                title="Editar"
                                            >
                                                <MdEdit className="text-base" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    handleDelete(pipeline)
                                                }}
                                                title="Excluir"
                                                disabled={
                                                    (pipeline._count?.deals ??
                                                        0) > 0
                                                }
                                            >
                                                <MdDelete className="text-base" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 text-secondary group-hover:translate-x-1 transition-transform">
                                        <span className="text-xs font-medium">
                                            Ver funil
                                        </span>
                                        <MdArrowForward className="text-sm" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create / Edit Modal */}
            {showModal && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingPipeline
                                    ? 'Editar Funil'
                                    : 'Criar Funil'}
                            </h3>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setShowModal(false)}
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="form-control w-full sm:col-span-2">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Nome *
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: Funil de Aquisição"
                                        value={formName}
                                        onChange={e =>
                                            setFormName(e.target.value)
                                        }
                                        autoFocus
                                    />
                                </div>

                                <div className="form-control w-full sm:col-span-2">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Quem entra neste funil
                                        </span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Ex.: interessados em cirurgia plástica estética — lipo, abdominoplastia, próteses, rinoplastia."
                                        rows={3}
                                        value={formDescription}
                                        onChange={e =>
                                            setFormDescription(e.target.value)
                                        }
                                    />
                                    <label className="label pt-1.5 pb-0 whitespace-normal">
                                        <span className="label-text-alt text-base-content/60 whitespace-normal leading-snug">
                                            A IA usa este texto para decidir em qual funil colocar cada contato.
                                        </span>
                                    </label>
                                </div>

                                <div className="form-control w-full sm:col-span-2">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Filial
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formCompanyId ?? ''}
                                        onChange={e => {
                                            const id = e.target.value ? parseInt(e.target.value) : null
                                            setFormCompanyId(id)
                                            if (!id) setFormIsUnitDefault(false)
                                        }}
                                    >
                                        <option value="">
                                            Nenhuma — funil da organização inteira
                                        </option>
                                        {companies.map(company => (
                                            <option key={company.id} value={company.id}>
                                                {company.tradingName || company.name}
                                            </option>
                                        ))}
                                    </select>
                                    <label className="label pt-1.5 pb-0 whitespace-normal">
                                        <span className="label-text-alt text-base-content/60 whitespace-normal leading-snug">
                                            {formCompanyId
                                                ? 'A oportunidade assume a filial do funil em que está.'
                                                : 'Sem filial, o funil vale para a organização inteira — como a triagem, o follow-up ou o overflow.'}
                                        </span>
                                    </label>
                                </div>

                                <div className="flex flex-col gap-2">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Tipo
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formType}
                                        onChange={e =>
                                            setFormType(
                                                e.target.value as PipelineType
                                            )
                                        }
                                    >
                                        <option value="ACQUISITION">
                                            Aquisição
                                        </option>
                                        <option value="RETENTION">
                                            Retenção
                                        </option>
                                        <option value="UPSELL">Upsell</option>
                                        <option value="CUSTOM">
                                            Personalizado
                                        </option>
                                    </select>
                                </div>

                                    <div title="Quem chega pelos canais escolhidos entra direto neste funil, sem triagem, e só sai movido por uma pessoa. Cada canal tem um funil: escolher aqui tira o canal do outro.">
                                        <MultiSelectFilter
                                            label="Padrão do tráfego pago"
                                            options={PAID_TRAFFIC_CHANNELS.map(channel => {
                                                const owner = pipelines.find(
                                                    p =>
                                                        p.id !== editingPipeline?.id &&
                                                        pipelinePaidTrafficChannels(p).includes(channel)
                                                )
                                                return {
                                                    value: channel,
                                                    label: owner
                                                        ? `${PAID_TRAFFIC_CHANNEL_LABELS[channel]} (hoje em ${owner.name})`
                                                        : PAID_TRAFFIC_CHANNEL_LABELS[channel],
                                                }
                                            })}
                                            value={formPaidTrafficChannels}
                                            onChange={values =>
                                                setFormPaidTrafficChannels(
                                                    PAID_TRAFFIC_CHANNELS.filter(c => (values ?? []).includes(c))
                                                )
                                            }
                                            emptyLabel="Nenhum canal"
                                            allSelectedLabel="Todos os canais"
                                            selectAllLabel="Todos os canais"
                                            menuMinWidth={280}
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3 justify-center">
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={formIsDefault}
                                            onChange={e =>
                                                setFormIsDefault(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <span className="label-text">
                                            Padrão da organização (triagem)
                                        </span>
                                    </label>
                                    <label
                                        className={`label cursor-pointer justify-start gap-3 ${formCompanyId ? '' : 'opacity-50'}`}
                                        title={formCompanyId ? '' : 'Escolha uma filial para marcar'}
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={formIsUnitDefault}
                                            disabled={!formCompanyId}
                                            onChange={e =>
                                                setFormIsUnitDefault(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <span className="label-text">
                                            Padrão da filial
                                        </span>
                                    </label>
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={formAllowMultipleDeals}
                                            onChange={e =>
                                                setFormAllowMultipleDeals(
                                                    e.target.checked
                                                )
                                            }
                                        />
                                        <span className="label-text">
                                            Permitir múltiplos negócios por lead
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <PipelineDistributionFields
                                ownerUserId={formOwnerUserId}
                                onOwnerChange={setFormOwnerUserId}
                                distributionGroupId={formDistributionGroupId}
                                onGroupChange={setFormDistributionGroupId}
                                currentOwner={editingPipeline?.owner}
                                savedGroupId={editingPipeline?.distributionGroupId}
                                onGroupsChanged={loadPipelines}
                            />

                            <div className="pt-4 border-t border-base-300/50">
                                <div className="flex items-center gap-2 mb-1">
                                    <MdLock className="text-lg text-secondary" />
                                    <h4 className="font-semibold text-base-content">
                                        Perfis que podem ver este funil
                                    </h4>
                                </div>
                                <p className="text-xs text-base-content/60 leading-snug mb-4">
                                    Veem este funil e as oportunidades dele a dona, os perfis marcados aqui e os
                                    perfis que veem todos os funis, que aparecem travados na lista e só mudam em
                                    Vendas &gt; Configurações. Contas MASTER e COMPANY sempre veem.
                                </p>
                                <StorageRolePicker
                                    selected={formRoleIds}
                                    onChange={setFormRoleIds}
                                    lockedIds={viewAllRoleIds}
                                    lockedLabel="Vê todos os funis"
                                    disabled={submitting}
                                    emptyMessage="Nenhum perfil de acesso cadastrado. Crie perfis em Segurança > Permissões antes de restringir o funil."
                                    helpText={
                                        formRoleIds.length > 0 || formOwnerUserId
                                            ? 'Além dos administradores e dos perfis que veem todos os funis, veem este funil a dona e os perfis marcados.'
                                            : 'Sem dona e sem perfis: só administradores e os perfis que veem todos os funis enxergam este funil.'
                                    }
                                />
                            </div>

                            {/* Initial Stages (only for creation) */}
                            {!editingPipeline && (
                                <div className="pt-4 border-t border-base-300/50">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-semibold text-base-content">
                                            Etapas Iniciais
                                        </h4>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline btn-secondary gap-1"
                                            onClick={handleAddStage}
                                        >
                                            <MdAdd className="text-base" />
                                            Etapa
                                        </button>
                                    </div>

                                    {formStages.length === 0 ? (
                                        <p className="text-sm text-base-content/50 text-center py-4">
                                            Nenhuma etapa definida. O funil será
                                            criado sem etapas.
                                        </p>
                                    ) : (
                                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                            {formStages.map((stage, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center gap-2 bg-base-200/50 rounded-lg p-2"
                                                >
                                                    <input
                                                        type="color"
                                                        className="w-8 h-8 rounded cursor-pointer border-0"
                                                        value={
                                                            stage.color ||
                                                            '#6B7280'
                                                        }
                                                        onChange={e =>
                                                            handleStageChange(
                                                                index,
                                                                'color',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm flex-1"
                                                        placeholder="Nome da etapa"
                                                        value={stage.name}
                                                        onChange={e =>
                                                            handleStageChange(
                                                                index,
                                                                'name',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        type="number"
                                                        className="input input-bordered input-sm w-20"
                                                        placeholder="%"
                                                        min={0}
                                                        max={100}
                                                        value={
                                                            stage.probability ??
                                                            ''
                                                        }
                                                        onChange={e =>
                                                            handleStageChange(
                                                                index,
                                                                'probability',
                                                                parseInt(
                                                                    e.target
                                                                        .value
                                                                ) || 0
                                                            )
                                                        }
                                                        title="Probabilidade %"
                                                    />
                                                    <div className="flex items-center gap-1">
                                                        <label
                                                            className="cursor-pointer"
                                                            title="Etapa de ganho"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-xs checkbox-success"
                                                                checked={
                                                                    stage.isWonStage ||
                                                                    false
                                                                }
                                                                onChange={e =>
                                                                    handleStageChange(
                                                                        index,
                                                                        'isWonStage',
                                                                        e.target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                        <label
                                                            className="cursor-pointer"
                                                            title="Etapa de perda"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-xs checkbox-error"
                                                                checked={
                                                                    stage.isLostStage ||
                                                                    false
                                                                }
                                                                onChange={e =>
                                                                    handleStageChange(
                                                                        index,
                                                                        'isLostStage',
                                                                        e.target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        </label>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-xs text-error"
                                                        onClick={() =>
                                                            handleRemoveStage(
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <MdClose className="text-sm" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <p className="text-xs text-base-content/50 mt-2">
                                        Defina as etapas do funil. Marque os
                                        checks verdes para etapa de ganho e
                                        vermelhos para etapa de perda.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowModal(false)}
                                disabled={submitting}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleSubmit}
                                disabled={submitting || !formName.trim()}
                            >
                                {submitting ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : editingPipeline ? (
                                    'Salvar'
                                ) : (
                                    'Criar'
                                )}
                            </button>
                        </div>
                    </div>
                </dialog>
            )}

            {/* Delete Confirmation */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setPipelineToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir funil"
                message={`Tem certeza de que deseja excluir o funil "${pipelineToDelete?.name}"? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
